import Glider from 'glider-js';

/**
 * Manages the autoplay functionality for a Glider carousel
 */
function autoplayGlider(glider: Glider, interval: number) {
  const totalSlides = glider.track.childElementCount;
  let slideTimeout: NodeJS.Timeout;
  let nextIndex = 1;
  let pause = true;

  function slide() {
    slideTimeout = setTimeout(() => {
      if (nextIndex >= totalSlides) {
        nextIndex = 0;
      }
      glider.scrollItem(nextIndex, true);
      nextIndex += 1;
    }, interval);
  }

  function setupEventListeners() {
    glider.ele.addEventListener('glider-animated', () => {
      window.clearInterval(slideTimeout);
      slide();
    });

    glider.ele.addEventListener('mouseover', () => {
      if (pause) {
        clearInterval(slideTimeout);
        pause = false;
      }
    });

    glider.ele.addEventListener('mouseout', () => {
      if (!pause) {
        slide();
        pause = true;
      }
    });
  }

  setupEventListeners();
  slide();
}

/**
 * Handles dot navigation visibility based on slide count
 */
function manageDotNavigation(element: HTMLDivElement) {
  const totalSlides = element.querySelector('.glider-track')?.children.length ?? 0;
  const dots = element.nextElementSibling as HTMLElement;

  if (totalSlides < 2 && dots) {
    dots.style.display = 'none';
  }
}

/**
 * Creates Glider configuration options based on element attributes
 */
function createGliderConfig(element: HTMLDivElement): Glider.Options {
  return {
    slidesToScroll: Number(element.dataset.gliderSlidesToShow) || 1,
    slidesToShow: 1,
    skipTrack: true,
    scrollLock: true,
    dots: element.nextElementSibling,
    responsive: [
      {
        breakpoint: 768, // Bootstrap `md` breakpoint
        settings: {
          slidesToShow: Number(element.dataset.gliderSlidesToShowMd) || 1,
        },
      },
    ],
  };
}

/**
 * Initializes a single Glider carousel
 */
function initGlider(element: HTMLDivElement) {
  // Add loaded class when glider is ready
  element.addEventListener('glider-loaded', () => {
    element.classList.add('loaded');
  });

  // Create and initialize the Glider instance
  const gliderConfig = createGliderConfig(element);
  const glider = new Glider(element, gliderConfig);

  // Manage dot navigation visibility
  manageDotNavigation(element);

  // Set up autoplay if configured
  const autoplayInterval = Number(element.dataset.gliderAutoplayInterval);
  if (autoplayInterval) {
    autoplayGlider(glider, autoplayInterval);
  }
}

/**
 * Initialize all Glider carousels on page load
 */
function initAllGliders() {
  const gliderElements = document.querySelectorAll<HTMLDivElement>('.glider');
  gliderElements.forEach(initGlider);
}

// Initialize gliders when DOM is ready
window.addEventListener('DOMContentLoaded', initAllGliders);
